import { Navigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { isInFreeTrial } from "@/lib/freeTrial";

interface ProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * Route protégée qui vérifie :
 * 1. L'utilisateur est connecté
 * 2. L'utilisateur n'est pas bloqué (paiement échoué / annulé)
 * 3. L'utilisateur a un abonnement actif, un essai en cours, OU un accès invité
 */
export const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const { user, loading: authLoading } = useAuth();
  const { subscribed, isTrialing, isBlocked, loading: subscriptionLoading } = useSubscription();

  // Attendre que les données auth soient chargées
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Pas connecté → redirection vers auth
  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // Vérifier l'accès invité dans les métadonnées utilisateur
  const hasInviteAccess = user.user_metadata?.has_invite_access === true;

  // Attendre que les données d'abonnement soient chargées
  if (subscriptionLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  // NOUVEAU: Vérifier si l'utilisateur est bloqué (paiement échoué)
  // Les invités ne sont jamais bloqués
  if (isBlocked && !hasInviteAccess) {
    return <Navigate to="/payment-blocked" replace />;
  }

  // Vérifier le free trial sans CB (basé sur user_metadata)
  const hasFreeTrial = isInFreeTrial(user);

  // Vérifier si l'utilisateur a accès
  const hasAccess = subscribed || isTrialing || hasInviteAccess || hasFreeTrial;

  // Pas d'abonnement, pas d'essai, pas de code invité, pas de free trial → redirection vers payment-setup
  if (!hasAccess) {
    return <Navigate to="/payment-setup" replace />;
  }

  return <>{children}</>;
};
